<template>
<div class="chapter">
<div class="inner_page">
<p class="chap-title1">Introduction</p>
<p class="noindent">The legend of Wyoming’s forgotten women’s militia hearkens to Wyoming statehood and the women’s equality movement. The territory was founded in July 1867 and was branded a maverick in 1869 when John A. Campbell, Wyoming’s first territorial governor, signed a bill granting women the right to vote. When Wyoming petitioned for statehood in 1889, the issue of women’s suffrage was a hot button topic divided by two parties of public opinion. Many prominent Cheyenne citizens favored suffrage and statehood, but one dissenter was a prominent Cheyenne judge whose daughter was a member of Company K. The judge threatened to “move to Alaska if Wyoming became a state.”</p>
<p class="indent">The first legislative assembly on December 10, 1869, endorsed the women’s suffrage movement and included women’s right to vote in the Wyoming constitution:</p>
<div class="Ext">
<p class="noindent"><i>Section 1.—Every woman of the age of twenty-one years residing in this territory, may, at any election to be holden under the laws thereof, cast her vote. And her right of the election franchise and to hold office shall be the same under the election laws of the Territory as those of electors.</i></p>
</div>
<p class="indent">The section in the state constitution on this subject read:</p>
<div class="Ext">
<p class="noindent"><i>(Section 1, Article VI.)</i></p>
<p class="noindent"><i>The rights of citizens of the State of Wyoming to vote and hold office</i> <i>shall not be denied or abridged on account of sex. Both male and female citizens of this State shall enjoy equally all civil, political and religious</i> <i>rights and privileges.</i></p>
</div>
<p class="indent">Section 27 of the state constitution said: “Elections shall be open, free and equal, and no power, civil or military, shall at any time interfere to prevent an untrammeled exercise of the right of suffrage.”</p>
<p class="indent">Shortly after Wyoming women were granted the right to vote, Wyoming appointed Esther Hobart Morris as the first woman justice of the peace in February 1870. She was followed by the first all-woman jury, the first woman to serve as a bailiff (Martha Symons Boies Atkinson) and the first woman in the nation to vote in a general election (Louisa Ann Swain).</p>
<p class="indent">The constitution, however, exempted women from military service. Article 17, section 1 said, “The militia of the State shall consist of male citizens of the State, between the ages of eighteen and forty-five years.” Contrary to this, Company K paved new ground as the first and only fully trained women’s militia in the nation. Company K earned major ink from coast to coast as its performances were reported throughout Wyoming and in major newspapers in Washington, D.C.; Pittsburgh; Denver; Omaha; Santa Fe, New Mexico; Astoria, Oregon; and Sacramento, California. A Wyoming newspaper editorial suggested sending the military maidens to Washington to “show our statesmen what the girls can do in the militia…and admit that the women of Wyoming at least were deserving of statehood.”</p>
<p class="indent">Some 125 years later, the legacy of the Girl Guard has become a footnote found in a manila file at the Wyoming State Archives in Cheyenne. I became interested in the Wyoming Girl Guard militia while working at the F.E. Warren Air Force Base museum. One day, while I was looking at a photograph of the Seventeenth Infantry marching on the parade field at Fort D.A. Russell, my boss, Paula Taylor, asked me if I could see the Girl Guards in the background. I could, but they were barely visible. The Wyoming Girl Guard militia earned its ink when Wyoming marched to statehood, but post-statehood coverage of the event was minimal, even at the twenty-fifth and fiftieth anniversaries of statehood. The Wyoming Historical Society featured a sidebar on Company H in the 1965 edition of the <i>Annals of Wyoming</i>, but a complete history has never been written.</p>
<p class="indent">I have a confession: I never intended to write a book about the Wyoming Girl Guard Militia. When I began researching its history to satisfy my curiosity, I saw two sepia-toned photographs that started me on this journey. This manuscript is dedicated to the memory of the unsung heroines of the Wyoming Girl Guard Militia.</p>
<div class="Ext">
<p class="noindent"><i>“The Girl Guards”</i></p>
<p class="noindent"><i>Have you seen our “Girl Guards,” known as Company K?</i></p>
<p class="noindent"><i>With their miniature muskets and caps of gray!</i></p>
<p class="noindent"><i>Have you heard the tramp of their delicate feet</i></p>
<p class="noindent"><i>Keeping perfect step to a cadence sweet?</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>Enchantingly sweeping in fitful air</i></p>
<p class="noindent"><i>The kaleidoscope march of these maidens fair;</i></p>
<p class="noindent"><i>Who forth from a land of Elysian bloom</i></p>
<p class="noindent"><i>To pass in review only seem to have come.</i></p>
<p class="noindent"><i>O, what can compare with their beauty and worth</i></p>
<p class="noindent"><i>But the Deity’s smile on the bosom of earth!</i></p>
<p class="noindent"><i>Just heard, did you say? Then I’ll tell you more,</i></p>
<p class="noindent"><i>Concerning each one of this bright twenty-four.</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>Stand here close by me where the footlights burn,</i></p>
<p class="noindent"><i>Where Company K makes many a turn;</i></p>
<p class="noindent"><i>To the right or to left, as the case may be,</i></p>
<p class="noindent"><i>Not as they respond to the reveille</i></p>
<p class="noindent"><i>Will you see them, but each in her proper place</i></p>
<p class="noindent"><i>In line, when they move with such charming grace,</i></p>
<p class="noindent"><i>And as they appear by the tactical grade</i></p>
<p class="noindent"><i>In the practice drill or a grand parade.</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>Gallant Colonel Stitzer is in command,</i></p>
<p class="noindent"><i>You have heard of him oft—stay, the girls are at hand;</i></p>
<p class="noindent"><i>First Emma O’Brien and Kate Kelly too,</i></p>
<p class="noindent"><i>Brighter, nobler girls Cheyenne never knew;</i></p>
<p class="noindent"><i>Mary Davidson, handsome—in girlhood a queen,</i></p>
<p class="noindent"><i>The intellect flash in her eye to be seen;</i></p>
<p class="noindent"><i>With fair Gertrude Morgan make up the full set.</i></p>
<p class="noindent"><i>Yet no; I mean the first Girl Guard quartette.</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>Next come Lulu Maxwell and Alvenie Gloye,</i></p>
<p class="noindent"><i>And then Gertrude Ellis and sweet Mamie Horrie,</i></p>
<p class="noindent"><i>As charming a four you will scarce ever see</i></p>
<p class="noindent"><i>These are second in line in K Company;</i></p>
<p class="noindent"><i>Handsome Eva Smalley with wealth of dark hair,</i></p>
<p class="noindent"><i>With Iona Davis, both winsome and fair,</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>And then Edna Wilseck and Carrie Ingram,</i></p>
<p class="noindent"><i>Two bright, charming misses, in unison come.</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>Three fours now have passed us—just one-half in all—</i></p>
<p class="noindent"><i>As they now countermarch through the well lighted hall.</i></p>
<p class="noindent"><i>Look! look you again! as the column sweeps on!</i></p>
<p class="noindent"><i>Here come Ada Johnston and Clara Newman,</i></p>
<p class="noindent"><i>Two as pretty young misses as ever you’ll see,</i></p>
<p class="noindent"><i>While the fair Isabel Montgomery</i></p>
<p class="noindent"><i>And sweet Bessie Vreeland, excelling in song,</i></p>
<p class="noindent"><i>With steps of perfection, come marching along.</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>Then Jessie Lee, a bright, fair-haired lass,</i></p>
<p class="noindent"><i>Ora Cowhick, a beauty, quick near us pass;</i></p>
<p class="noindent"><i>Then Mina McGregor, both pretty and bright,</i></p>
<p class="noindent"><i>And Mamie Geotz, charming, with footsteps light;</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>Next comes Gracie Chaffin, sweet-voiced and fair,</i></p>
<p class="noindent"><i>And then Florence Bradley with queenly air;</i></p>
<p class="noindent"><i>While Jessie Newman, a pretty young girl,</i></p>
<p class="noindent"><i>With Effie Vreeland, in beauty a pearl,</i></p>
<p class="noindent"><i>Sweep past in the march on the waxened floor;</i></p>
<p class="noindent"><i>They’re last, but not least of the fair twenty-four.</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>All honor then give to our Company K;</i></p>
<p class="noindent"><i>But the story I’ve told, and further will say</i></p>
<p class="noindent"><i>While specially mentioning few at this time</i></p>
<p class="noindent"><i>I’m obliged to do this if the story’s in rhyme,</i></p>
<p class="noindent"><i>And I’ve hoped as I witnessed their marching to-night</i></p>
<p class="noindent"><i>That the future to each one may e’er seem as bright</i></p>
<p class="noindent"><i>And be but the reflex of what now appears</i></p>
<p class="noindent"><i>As they march down the aisles of the untrodden years;</i></p>
</div>
<div class="Ext">
<p class="noindent"><i>May to these gems in girlhood a bountiful share</i></p>
<p class="noindent"><i>Be vouchsafed of Divine and Omnipotent care:</i></p>
<p class="noindent"><i>For by such to this life there is graciously given</i></p>
<p class="noindent"><i>A beauteous charm earth has borrowed from Heaven.</i><sup><span href="#/Notes#FN1" id="FT1" title="Link target outside of Sample">1</span></sup></p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Introduction.vue?5bf0279e